// ============================================================
// agent_expiry_cards.js — 위임 만료·철회 시스템 카드 판정(순수 로직)
// ============================================================
// 역할: 런타임 SSE 이벤트(status / tool_call)의 종료 사유를 보고, 채팅에 띄울
//       "왜 안 됐는지 + 무엇을 하면 되는지" 시스템 카드 종류를 판정한다.
//
// 왜 컨트롤러 밖으로 뺐나:
//   판정 규칙(사유 코드 → 카드 종류)이 이 3층 UX 의 계약 그 자체라 회귀가 나면
//   사용자가 다시 "반응 없음"을 겪는다. DOM·Stimulus 없이 검증할 수 있도록
//   순수 함수로 분리해 node 테스트(test/javascript/agent_expiry_cards_test.js)가 직접 태운다.
//
// 사유 코드 출처(agent-runtime, 무접촉):
//   - 시작 실패: status 이벤트 { status:'error',   stopReason:'need_consent'|'token_acquire_failed' }
//   - 중도 철회: status 이벤트 { status:'stopped', stopReason:'interrupted' } +
//                직전 tool_call { status:'denied', reason:'delegation_revoked'|'slot_user_mode'|... }
//
// 주의사항:
//   - 반환 labelKey 는 i18n 키 이름(agent_chats.expiry.<labelKey>)이다. 문구를 여기 두지 않는다.
//   - 목록 밖 사유는 반드시 null 을 돌려준다 — 정상 종료(completed)에 카드가 뜨면 회귀다.
// ============================================================

// 카드 종류. cta=true 면 재동의 딥링크 버튼을 함께 그린다.
const EXPIRED_CARD   = { labelKey: "expired_card",   cta: true }
const REVOKED_CARD   = { labelKey: "revoked_card",   cta: true }
const USER_MODE_CARD = { labelKey: "user_mode_card", cta: false }

// status 이벤트에서 "재동의가 필요한" 종료 사유.
// need_consent=위임 없음/만료, token_acquire_failed=위임은 있으나 토큰 발급 실패(만료·비활성).
const STATUS_REASONS = {
  need_consent:         EXPIRED_CARD,
  token_acquire_failed: EXPIRED_CARD
}

// tool_call denied 의 사유별 카드.
// token_* 는 위임 토큰이 더 이상 유효하지 않은 상태라 만료와 같은 안내로 묶는다.
const TOOL_CALL_REASONS = {
  delegation_revoked:  REVOKED_CARD,
  introspect_inactive: REVOKED_CARD,
  token_decode_failed: EXPIRED_CARD,
  token_failed:        EXPIRED_CARD,
  need_consent:        EXPIRED_CARD,
  slot_user_mode:      USER_MODE_CARD
}

// status 이벤트가 종료를 뜻하는 값(그 외 running/waiting_confirm 등에는 카드를 띄우지 않는다).
const TERMINAL_STATUSES = [ "error", "stopped" ]

// ── status 이벤트 → 카드 판정 ────────────────────────────────
// @param data {Object} SSE status 이벤트 payload({ status, stopReason })
// @returns {{labelKey: string, cta: boolean}|null} 카드가 필요 없으면 null
export function statusCard(data) {
  if (!data || !TERMINAL_STATUSES.includes(data.status)) return null
  return STATUS_REASONS[data.stopReason] || null
}

// ── tool_call 이벤트 → 카드 판정 ─────────────────────────────
// denied 가 아닌 툴콜(ok/error/pending_confirm)은 카드 대상이 아니다.
// @param data {Object} SSE tool_call 이벤트 payload({ status, reason })
// @returns {{labelKey: string, cta: boolean}|null} 카드가 필요 없으면 null
export function toolCallCard(data) {
  if (!data || data.status !== "denied") return null
  return TOOL_CALL_REASONS[data.reason] || null
}

// ── run 당 1회 게이트 ────────────────────────────────────────
// WHY: 중도 철회는 tool_call(denied) → status(stopped) 가 연달아 오므로, 두 핸들러가
//      각각 카드를 그리면 같은 안내가 2번 쌓인다. run(=EventSource 연결) 하나당 1장만 통과시킨다.
// 사용: 새 run 을 열 때 reset(), 카드를 그리기 직전 take() 로 통과 여부를 묻는다.
export class ExpiryCardGate {
  constructor() { this._used = false }

  // @returns {Boolean} 이 run 에서 아직 카드를 안 그렸으면 true(그리고 소모한다).
  take() {
    if (this._used) return false
    this._used = true
    return true
  }

  reset() { this._used = false }
}
